import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography } from '@mui/material';
import { EventBusyOutlined, ChevronRight, Close as CloseIcon } from '@mui/icons-material';
import comms from '../comms';
import TripModal from '../TripModal';
import RouteItineraryDetail from './RouteItineraryDetail';
import { classifyTripStatus } from '../../utils/tripStatusClassification';

/**
 * "My Schedule" — the driver's ASSIGNED, still-to-do work for a rolling 15-day
 * window (today .. today+15), grouped by day.
 *
 * Deliberately NOT the Bookings screen: no Schedule/Upcoming/Completed scopes, no
 * SCHEDULED/ACTIVE sub-tabs, no single-day dropdown. Scheduled work only —
 * in-progress and finished trips stay in Bookings.
 *
 * Data: /API/Driver/Schedule takes a SINGLE date (comms.getDriverSchedule) — there is
 * no date-range variant on any Driver endpoint (only /Receipts takes fromDate/toDate,
 * which is unrelated). So the window is assembled client-side, one request per day,
 * the same approach the Bookings "Upcoming" scope already uses. Requests are issued
 * in small batches rather than all 16 at once to avoid hammering the fleet API.
 */

const WINDOW_DAYS = 15; // rolling: today .. today + WINDOW_DAYS
const BATCH_SIZE = 4;

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDateForAPI(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function dayHeading(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  const label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  if (diff === 0) return `Today · ${label}`;
  if (diff === 1) return `Tomorrow · ${label}`;
  return label;
}

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

const addr = (t, lineKey, l1, l2) => t[lineKey] || `${t[l1] || ''} ${t[l2] || ''}`.trim();

/** A flag trip carries no booked passenger identity — server names it "Flag Trip". */
function tripKind(t) {
  const name = String(t.PassengerName || t.passengerName || '').trim().toLowerCase();
  return name === 'flag trip' ? 'Flag' : 'Assigned';
}

// Every schedule item is a real <button>, so it is focusable and fires on both
// Enter and Space for free. focus-visible gives keyboard users the same clear
// target the press state gives touch users.
const cardCls =
  'block w-full rounded-2xl bg-white p-3 text-left shadow-sm transition-transform ' +
  'hover:bg-gray-50 active:scale-[0.99] focus:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

/** Run promise-producing tasks in fixed-size batches, preserving order. */
async function inBatches(items, size, fn) {
  const out = [];
  for (let i = 0; i < items.length; i += size) {
    const slice = items.slice(i, i + size);
    // eslint-disable-next-line no-await-in-loop
    out.push(...(await Promise.all(slice.map(fn))));
  }
  return out;
}

function MySchedule({ userSettings, fleetSettings, onRegisterRefresh } = {}) {
  const [days, setDays] = useState([]); // [{ date, trips: [], route: {}|null }]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  // { date, routeName, schedule } for the route whose itinerary is open.
  const [selectedRoute, setSelectedRoute] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const stored = JSON.parse(localStorage.getItem('userSettings') || '{}');
      const driverId = userSettings?.Id || stored?.Id;
      if (!driverId) throw new Error('User not found');

      const dates = [];
      for (let i = 0; i <= WINDOW_DAYS; i++) dates.push(addDays(new Date(), i));

      const results = await inBatches(dates, BATCH_SIZE, (d) =>
        comms.getDriverSchedule(driverId, formatDateForAPI(d)).catch(() => null)
      );

      const built = results.map((res, idx) => {
        const date = dates[idx];
        if (!res?.status) return { date, trips: [], route: null };

        // Trips can arrive top-level or nested under schedule.
        const rawTrips = Array.isArray(res.trips)
          ? res.trips
          : Array.isArray(res.schedule?.trips)
            ? res.schedule.trips
            : [];

        // SCHEDULED only — in-progress and finished work belongs in Bookings.
        const trips = rawTrips.filter((t) => classifyTripStatus(t.TripStatus) === 'scheduled');

        // Route/itinerary days (fixed & looped shuttle routes) carry their stops in
        // schedule.itinerary rather than schedule.trips. That is still assigned work,
        // so surface it as a one-line summary — but only when the day has no trips of
        // its own (matching Bookings' "Upcoming" scope) and the route has not started
        // yet, to honour "scheduled only".
        const itinerary = res.schedule?.itinerary;
        const route =
          rawTrips.length === 0 &&
          Array.isArray(itinerary) &&
          itinerary.length > 0 &&
          !res.schedule?.routeStarted
            ? {
                routeName: res.schedule.routeName || 'Route',
                startDateTime: res.schedule.startDateTime,
                endDateTime: res.schedule.endDateTime,
                stepCount: itinerary.length,
                // Kept whole so tapping the card can hand the same shape to
                // RouteItineraryDetail that the Bookings screen renders.
                schedule: res.schedule,
              }
            : null;

        return { date, trips, route };
      });

      setDays(built);
    } catch (err) {
      setError(err.message || 'Could not load your schedule');
      setDays([]);
    } finally {
      setLoading(false);
    }
  }, [userSettings]);

  useEffect(() => {
    load();
  }, [load]);

  // Let the app bar's refresh icon drive this screen (same contract Scheduling uses).
  useEffect(() => {
    if (typeof onRegisterRefresh !== 'function') return undefined;
    onRegisterRefresh(() => load);
    return () => onRegisterRefresh(null);
  }, [onRegisterRefresh, load]);

  const populatedDays = days.filter((d) => d.trips.length > 0 || d.route);
  const totalItems = populatedDays.reduce(
    (n, d) => n + d.trips.length + (d.route ? 1 : 0),
    0
  );

  return (
    <div className="app-page">
      <div className="mb-4 border-b border-gray-200 pb-2">
        <h2 className="text-base font-bold text-gray-900">My Schedule</h2>
        <p className="text-xs text-gray-500">
          Assigned work for the next {WINDOW_DAYS} days
          {totalItems > 0 ? ` · ${totalItems} item${totalItems === 1 ? '' : 's'}` : ''}
        </p>
      </div>

      {loading && (
        <div className="rounded-lg bg-white p-8 text-center shadow">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500" />
          <p className="text-sm text-gray-600">Loading your schedule…</p>
        </div>
      )}

      {!loading && error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {error}
        </div>
      )}

      {!loading && !error && populatedDays.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <EventBusyOutlined style={{ fontSize: 56 }} className="text-gray-300" />
          <h3 className="mt-3 text-base font-semibold text-gray-800">
            No scheduled trips in the next {WINDOW_DAYS} days
          </h3>
          <p className="mt-1 max-w-xs text-sm text-gray-500">
            Assigned work will appear here as dispatch schedules it.
          </p>
        </div>
      )}

      {!loading && !error && populatedDays.length > 0 && (
        <div className="flex flex-col gap-5">
          {populatedDays.map((day) => (
            <div key={day.date.toDateString()}>
              <div className="mb-2 text-sm font-bold text-gray-900">
                {dayHeading(day.date)}
              </div>

              <div className="flex flex-col gap-2">
                {day.route && (
                  <button
                    type="button"
                    onClick={() => setSelectedRoute({ ...day.route, date: day.date })}
                    aria-label={`Open details for ${day.route.routeName} on ${dayHeading(day.date)}`}
                    className={cardCls}
                  >
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-semibold text-gray-900">
                            {day.route.routeName}
                          </span>
                          <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                            Route
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-gray-600">
                          {formatTime(day.route.startDateTime)} – {formatTime(day.route.endDateTime)}
                          {day.route.stepCount
                            ? ` · ${day.route.stepCount} stop${day.route.stepCount === 1 ? '' : 's'}`
                            : ''}
                        </div>
                      </div>
                      <ChevronRight className="shrink-0 text-gray-400" fontSize="small" />
                    </div>
                  </button>
                )}

                {day.trips.map((trip, i) => {
                  const pickup = addr(trip, 'startAddress', 'Origin_AddressLine1', 'Origin_AddressLine2');
                  const dropoff = addr(trip, 'endAddress', 'Destination_AddressLine1', 'Destination_AddressLine2');
                  return (
                    <button
                      key={trip.Id || trip.TripNo || i}
                      type="button"
                      onClick={() => setSelectedTrip(trip)}
                      aria-label={`Open details for trip ${trip.TripNo || ''}`}
                      className={cardCls}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-semibold text-gray-900">
                          #{trip.TripNo || '—'}
                          {trip.PassengerName ? ` · ${trip.PassengerName}` : ''}
                        </span>
                        <span className="flex shrink-0 items-center gap-1">
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                            {tripKind(trip)}
                          </span>
                          <ChevronRight className="text-gray-400" fontSize="small" />
                        </span>
                      </div>

                      <div className="mt-1 flex items-start gap-2 text-xs">
                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-blue-600" />
                        <span className="min-w-0 flex-1 truncate text-gray-700">
                          <span className="font-semibold">
                            {formatTime(trip.OriginScheduledDate) || '—'}
                          </span>
                          {pickup ? ` · ${pickup}` : ''}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-start gap-2 text-xs">
                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-red-500" />
                        <span className="min-w-0 flex-1 truncate text-gray-700">
                          <span className="font-semibold">
                            {formatTime(trip.DestinationScheduledDate) || '—'}
                          </span>
                          {dropoff ? ` · ${dropoff}` : ''}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Route detail — the same itinerary table the Bookings "Schedule" scope
          renders, via the shared RouteItineraryDetail component. */}
      <Dialog
        open={Boolean(selectedRoute)}
        onClose={() => setSelectedRoute(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { maxHeight: '85dvh' } }}
      >
        <DialogTitle
          sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
        >
          <span>
            {/* component="span": DialogTitle is already an <h2>; a nested Typography
                defaulting to <h6> is invalid DOM nesting. */}
            <Typography variant="h6" component="span" sx={{ display: 'block' }}>
              {selectedRoute?.routeName}
            </Typography>
            {selectedRoute && (
              <Typography variant="caption" component="span" color="text.secondary">
                {dayHeading(selectedRoute.date)} · {formatTime(selectedRoute.startDateTime)} –{' '}
                {formatTime(selectedRoute.endDateTime)}
              </Typography>
            )}
          </span>
          <IconButton aria-label="close" onClick={() => setSelectedRoute(null)} sx={{ color: 'grey.500' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, bgcolor: 'grey.50' }}>
          {selectedRoute && (
            <RouteItineraryDetail schedule={selectedRoute.schedule} showHeader={false} />
          )}
        </DialogContent>
      </Dialog>

      <TripModal
        trip={selectedTrip}
        onClose={() => setSelectedTrip(null)}
        fleetSettings={fleetSettings}
        onSave={(updated) => {
          // Keep the day groups in sync when the modal edits a trip in place.
          setDays((prev) =>
            prev.map((d) => ({
              ...d,
              trips: d.trips.map((t) => (t.Id === updated.Id ? { ...t, ...updated } : t)),
            }))
          );
          setSelectedTrip((cur) => (cur ? { ...cur, ...updated } : cur));
        }}
      />
    </div>
  );
}

export default MySchedule;
